import { useEffect, useRef, useState } from 'react'
import { prefersReducedMotion } from '../../scoreCountUp'
import {
  centredScrollTop,
  nextCollapsedState,
  STICKY_BAR_HEIGHT,
} from './stickyCollapse'

/**
 * Sticky collapsed header wiring: `isCollapsed` swaps the docked bar in once the
 * hero rank block scrolls above the viewport top; `isUserRowVisible` fades the bar's user-specific
 * cluster out while the real row is on screen (the league name stays visible throughout). Attach
 * the returned refs to the hero rank block and the user's standings row.
 */
export function useStickyCollapse() {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isUserRowVisible, setIsUserRowVisible] = useState(true)
  const heroRankRef = useRef<HTMLDivElement>(null)
  const youRowRef = useRef<HTMLDivElement>(null)

  // Collapse the header into the sticky bar on scroll. The document itself scrolls here (this route
  // has no inner scroll container), so we read the hero rank block's viewport-relative bottom edge
  // on each frame and apply the hysteresis band. rAF-throttled; runs once up front so a route
  // entered already scrolled starts in the right state.
  useEffect(() => {
    const heroEl = heroRankRef.current
    if (!heroEl) return
    let frame = 0
    const update = () => {
      frame = 0
      const bottom = heroEl.getBoundingClientRect().bottom
      setIsCollapsed((prev) => nextCollapsedState(prev, bottom))
    }
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  // Duplicate suppression: while the user's real row is on screen, fade the bar's user cluster out.
  // The root is inset from the top by the bar height so a row sitting *behind* the docked bar still
  // reads as offscreen (the cluster stays visible until the real row clears the bar).
  useEffect(() => {
    const rowEl = youRowRef.current
    if (!rowEl || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      ([entry]) => setIsUserRowVisible(entry.isIntersecting),
      { rootMargin: `-${STICKY_BAR_HEIGHT}px 0px 0px 0px`, threshold: 0 }
    )
    observer.observe(rowEl)
    return () => observer.disconnect()
  }, [])

  // Tapping the sticky bar centres the user's row in the viewport. Smooth by default, instant under
  // reduced motion.
  const jumpToUserRow = () => {
    const rowEl = youRowRef.current
    if (!rowEl) return
    const rect = rowEl.getBoundingClientRect()
    window.scrollTo({
      top: centredScrollTop(rect, window.scrollY, window.innerHeight),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })
  }

  return {
    isCollapsed,
    isUserRowVisible,
    heroRankRef,
    youRowRef,
    jumpToUserRow,
  }
}
